import {
	Component,
	OnInit,
	ViewEncapsulation,
	OnDestroy,
	Output,
	EventEmitter,
	Input,
	ViewChildren,
	ElementRef,
	QueryList,
	NgZone
} from '@angular/core';
import { CdkDragDrop, moveItemInArray, transferArrayItem, CdkDropList, CdkDrag } from '@angular/cdk/drag-drop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription, debounceTime, distinctUntilChanged, take } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { NgFor, NgIf } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import { DatacenterViewService } from '../../services/datacenter-view.service';
import { pageTransitions } from '../../animations/page-transitions';
import { ColumnDefAndHeader, DatacenterType } from '../../models/datacenter-aggrid-view';
import { DatacenterDefaultConfiguration } from '../datacenter-default-configuration';
import { TranslationService } from '../../services/translation.service';
import { SlideButtonComponent } from '../../components/slide-button/slide-button.component';

import { ColumnAggregator } from './column-aggregator';

@Component({
	selector: 'copo-datacenter-save-layout',
	templateUrl: './datacenter-save-layout.component.html',
	styleUrl: './datacenter-save-layout.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		TranslateModule,
		CdkDropList,
		NgFor,
		CdkDrag,
		MatIconModule,
		NgIf,
		SlideButtonComponent,

		MatFormFieldModule,

		MatInputModule,
		FormsModule,
		ReactiveFormsModule,

		MatButtonModule
	]
})
export class DatacenterSaveLayoutComponent implements OnInit, OnDestroy {
	@Input() togglePosition = false;
	@Output() readonly typeOfTab = new EventEmitter<string>();
	@Output() readonly currentColumnStateForSorting = new EventEmitter<ColumnAggregator>();
	@Output() readonly togglePositionChange = new EventEmitter<boolean>();
	@ViewChildren('pinnedItem', { read: ElementRef }) pinnedColumnItems!: QueryList<ElementRef>;
	@ViewChildren('scrollableItem', { read: ElementRef }) scrollableColumnItems!: QueryList<ElementRef>;
	@ViewChildren('allItem', { read: ElementRef }) allColumnItems!: QueryList<ElementRef>;

	searchTextControl = new FormControl('');
	columnAggregatorList: ColumnAggregator;
	allColumns: ColumnDefAndHeader[];
	groupIds: string[];
	grabbedIndex: number | undefined;
	activeList: 'pinnedColumns' | 'scrollableColumns' | 'allColumns' | undefined;
	lastActionWasDrop = false;
	focusSub: Subscription | undefined;
	private searchTextSubscription?: Subscription;

	constructor(
		readonly viewService: DatacenterViewService,
		private readonly defaultConf: DatacenterDefaultConfiguration,
		private readonly router: Router,
		private readonly route: ActivatedRoute,

		private readonly snackBar: MatSnackBar,
		private readonly translationService: TranslationService,
		private readonly ngZone: NgZone,
	) {
		const { viewToEdit } = this.viewService;
		if (!viewToEdit) {
			throw new Error('no view to edit');
		}
		const datacenterType =
			<DatacenterType | undefined> this.route.snapshot.queryParams['type'] ?? viewToEdit.viewType;
		this.allColumns = this.defaultConf.getAllColumnsAndHeader(datacenterType).sort(
			(c1, c2) => c1.header.localeCompare(c2.header)
		);
		this.groupIds = [
			...new Set(this.allColumns.filter(item => item.groupId !== undefined)
				.map(item => item.groupId ?? ''))
		];
		const columns = viewToEdit.currentView.columnState.filter(col => !this.groupIds.includes(col.groupId ?? ''))
			.map(value => ({
				colId: value.colId,
				header: this.defaultConf.getHeaderForColumn(datacenterType, value.colId),
				pinned: value.pinned,
				sortable: this.defaultConf.getIsColumnSortable(datacenterType, value.colId)
			}));
		this.allColumns = this.allColumns.filter(col => !this.groupIds.includes(col.groupId ?? ''));

		this.columnAggregatorList = new ColumnAggregator(columns.length > 0
			? [...this.allColumns] : []);
		for (const column of columns) {
			this.columnAggregatorList.addVisibleColumn(column);
		}
	}

	ngOnInit() {
		this.searchTextSubscription = this.searchTextControl.valueChanges.pipe(
			debounceTime(300),
			distinctUntilChanged()
		).subscribe(value => {
			const searchText = <string | undefined> value;
			if (searchText === undefined) {
				return;
			}
			const allHiddenColumns = this.allColumns.filter(column => {
				const index = this.columnAggregatorList.getColumnsToShow().findIndex(col => col.colId === column.colId);
				return index === -1;
			});
			this.columnAggregatorList.allColumns = allHiddenColumns.filter(column => {
				if (column.header) {
					return column.header.toLowerCase().replace(/\u00A0/g, ' ').search(searchText.toLowerCase()) >= 0;
				}
				return false;
			});
		});
	}

	focusItem(listName: 'pinnedColumns' | 'scrollableColumns' | 'allColumns', index: number) {
		this.focusSub?.unsubscribe();
		this.focusSub = this.ngZone.onStable.pipe(take(1)).subscribe(() => {
			this.focusSub?.unsubscribe();
			this.focusSub = undefined;
			const listMap = {
				pinnedColumns: this.pinnedColumnItems,
				scrollableColumns: this.scrollableColumnItems,
				allColumns: this.allColumnItems
			};
			const el = <HTMLElement | undefined> listMap[listName].get(index)?.nativeElement;
			el?.focus();
			el?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
		});
	}

	onKeyDown(event: KeyboardEvent, listName: 'pinnedColumns' | 'scrollableColumns' | 'allColumns', index: number) {
		const list = this.columnAggregatorList[listName];
		const lists = <const> ['pinnedColumns', 'scrollableColumns', 'allColumns'];
		const currentListIndex = lists.indexOf(listName);

		switch (event.key) {
		case ' ':
		case 'Enter':
			if (this.grabbedIndex === undefined) {
				// Grab item
				this.grabbedIndex = index;
				this.activeList = listName;
			} else {
				// Drop item
				if (this.activeList === listName) {
					moveItemInArray(list, this.grabbedIndex, index);
					this.focusItem(listName, index); // 👈 restore focus after drop in same list
				} else if (this.activeList) {
					const fromList = this.columnAggregatorList[this.activeList];
					transferArrayItem(fromList, list, this.grabbedIndex, index);
					this.activeList = listName;
					this.grabbedIndex = index;
					this.focusItem(listName, index); // 👈 restore focus after cross-list drop
				}
				this.grabbedIndex = undefined;
				this.activeList = undefined;
			}
			event.preventDefault();
			break;

		case 'ArrowUp':
			if (this.grabbedIndex !== undefined && this.activeList === listName && index > 0) {
				moveItemInArray(list, index, index - 1);
				this.grabbedIndex = index - 1;
				this.focusItem(listName, this.grabbedIndex);
			}
			event.preventDefault();
			break;

		case 'ArrowDown':
			if (this.grabbedIndex !== undefined && this.activeList === listName && index < list.length - 1) {
				moveItemInArray(list, index, index + 1);
				this.grabbedIndex = index + 1;
				this.focusItem(listName, this.grabbedIndex);
			}
			event.preventDefault();
			break;

		case 'ArrowLeft':
		case 'ArrowRight':
			if (this.grabbedIndex !== undefined && this.activeList === listName) {
				const targetIndex =
					event.key === 'ArrowLeft' ? currentListIndex - 1 : currentListIndex + 1;
				if (targetIndex >= 0 && targetIndex < lists.length) {
					const targetListName = lists[targetIndex];
					const targetList = this.columnAggregatorList[targetListName];
					transferArrayItem(list, targetList, index, Math.min(index, targetList.length));
					this.activeList = targetListName;
					this.grabbedIndex = Math.min(index, targetList.length - 1);
					this.focusItem(this.activeList, this.grabbedIndex);
				}
			}
			event.preventDefault();
			break;
		case 'Escape':
			if (this.grabbedIndex !== undefined && this.activeList) {
				const itemIndex = this.grabbedIndex;
				const currentList = this.activeList;
				this.grabbedIndex = undefined;
				this.activeList = undefined;
				this.focusItem(currentList, itemIndex);
			}
			event.preventDefault();
			break;
		}
	}

	isGrabbed(listName: 'pinnedColumns' | 'scrollableColumns' | 'allColumns', index: number): boolean {
		return this.activeList === listName && this.grabbedIndex === index;
	}

	onDragStarted(listName: 'pinnedColumns' | 'scrollableColumns' | 'allColumns', index: number) {
		this.activeList = listName;
		this.grabbedIndex = index;
		this.lastActionWasDrop = false;
	}

	onDragEnded() {
		if (!this.lastActionWasDrop) {
			this.grabbedIndex = undefined;
			this.activeList = undefined;
		}
		this.lastActionWasDrop = false;
	}

	drop(event: CdkDragDrop<ColumnDefAndHeader[]>) {
		if (event.previousContainer === event.container) {
			moveItemInArray(
				event.container.data,
				event.previousIndex,
				event.currentIndex
			);
		} else {
			transferArrayItem(
				event.previousContainer.data,
				event.container.data,
				event.previousIndex,
				event.currentIndex
			);
			if (event.container.data === this.columnAggregatorList.pinnedColumns
				&& (event.container.data.length > 6
					|| event.container.data.some(data => this.groupIds.includes(data.colId)))) {
				this.snackBar.open(
					this.translationService.getTR(
						'datacenter.you-have-too-many-fixed-columns-to-add-a-new-one'
					),
					undefined,
					{
						duration: 5000,
					}
				);
				transferArrayItem(
					event.container.data,
					event.previousContainer.data,
					event.currentIndex,
					event.previousIndex
				);
			}
		}
		this.lastActionWasDrop = true;
		this.grabbedIndex = undefined;
		this.activeList = undefined;
	}

	onToggleChange() {
		this.typeOfTab.emit('sorting');
		this.currentColumnStateForSorting.emit(this.columnAggregatorList);
		this.togglePositionChange.emit(true);
	}

	getColumnState() {
		return this.columnAggregatorList.getColumnsToShow();
	}

	ngOnDestroy() {
		if (this.searchTextSubscription) {
			this.searchTextSubscription.unsubscribe();
		}
		this.focusSub?.unsubscribe();
	}

	async close() {
		await this.router.navigate(['../'], { relativeTo: this.route });
	}
}
